//external libs
import React, { ReactElement, memo, useMemo } from "react";
import classNames from "classnames";

//components
import { PaymentMethodIcon } from "@payment-ui/widgets/payment-method-icon";
import { ToggleSwitch } from "@payment-ui/widgets/toggle-switch";

//helpers
import { getCloudinaryUrl, AriaHidden, format } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/payment-core";

//styles
import styles from "./SplitPay.v4.scss";

// constants
import { TEXTS } from "@payment-ui/constants/index";

interface SplitPayProps {
    isSelected: boolean;
    isDisabled: boolean;
    inlineMessage?: string;
    paymentPageOfferEnabled?: boolean;
    splitPayData: {
        title: string;
        swiggyPayEligibleBalance: string;
        meta: {
            iconUrl: string;
            poweredByLogo?: string;
            poweredBy?: string;
            inlineMessage?: string;
        };
        rewardApplicable?: boolean;
    };
    onClick: VoidFunction;
    orderAmount: string;
    isPrimaryPaymentPage?: boolean;
}

const SPLITPAY_ICON_WIDTH = 32;

const SplitPay = ({
    isSelected,
    splitPayData,
    isDisabled,
    onClick,
    inlineMessage,
    paymentPageOfferEnabled,
    orderAmount,
    isPrimaryPaymentPage = false,
}: SplitPayProps): ReactElement | null => {
    const titleClassName = useMemo(() => {
        return classNames(styles.splitPayTitle, {
            [styles.splitPayTitleSelected]: isSelected,
            [styles.splitPayTitleDisabled]: isPrimaryPaymentPage && isDisabled, // changing the class only in case of L1 page for backward compatibilty
        });
    }, [isSelected, isDisabled, isPrimaryPaymentPage]);

    const subtitleClassName = useMemo(() => {
        return classNames(styles.splitPaySubtitle, {
            [styles.splitPaySubtitleDisabled]: isPrimaryPaymentPage && isDisabled, // changing the class only in case of L1 page for backward compatibilty
        });
    }, [isPrimaryPaymentPage, isDisabled]);

    const splitpayClass = useMemo(() => {
        return classNames(styles.splitPay, {
            [styles.splitPayCenter]: isEmpty(inlineMessage) || !isSelected,
        });
    }, [inlineMessage, isSelected]);

    const splitpayContentClass = useMemo(() => {
        return classNames(styles.splitPayContent, {
            [styles.splitPayContentOnL1]: isPrimaryPaymentPage,
        });
    }, [isPrimaryPaymentPage]);

    const splitPayCallout = useMemo(() => {
        if (!splitPayData?.rewardApplicable && !splitPayData?.meta?.inlineMessage) {
            return format(TEXTS.SWIGGY_PAY_CALLOUT, splitPayData.swiggyPayEligibleBalance, orderAmount);
        }
        return splitPayData.meta.inlineMessage;
    }, [
        splitPayData.rewardApplicable,
        splitPayData.meta.inlineMessage,
        splitPayData.swiggyPayEligibleBalance,
        orderAmount,
    ]);

    if (paymentPageOfferEnabled) {
        return (
            <div className={styles.paymentOffer}>
                <AriaHidden as={"div"} className={styles.paymentOfferImage}>
                    <img
                        crossOrigin="anonymous"
                        src={getCloudinaryUrl(splitPayData?.meta?.iconUrl ?? "")}
                        width={SPLITPAY_ICON_WIDTH}
                    />
                </AriaHidden>
                {!isEmpty(splitPayCallout) && <div className={styles.paymentOfferCallout}>{splitPayCallout}</div>}
            </div>
        );
    }

    return (
        <div className={splitpayClass}>
            <PaymentMethodIcon
                src={getCloudinaryUrl(splitPayData?.meta?.iconUrl ?? "", {
                    grayscale: isPrimaryPaymentPage && isDisabled,
                })}
                width={SPLITPAY_ICON_WIDTH}
                poweredByLogo={splitPayData.meta.poweredByLogo}
                poweredByIconAlt={splitPayData.meta.poweredBy}
            />
            <div className={splitpayContentClass}>
                <div className={titleClassName}>{splitPayData.title}</div>
                {(isSelected || isPrimaryPaymentPage) && <div className={subtitleClassName}>{inlineMessage}</div>}
            </div>
            {!isPrimaryPaymentPage && (
                <div className={styles.splitPayBalanceContainer}>
                    <div className={styles.splitPayBalance}>{splitPayData.swiggyPayEligibleBalance}</div>
                    <ToggleSwitch selected={isSelected} isDisabled={isDisabled} onClick={onClick} />
                </div>
            )}
        </div>
    );
};

export default memo(SplitPay);
